import { useEffect, useState } from "react";
import { api, MGR_ROLES } from "../api";
import { useAuth } from "../context/AuthContext";
import { useLang, fmtRM, fmtDate } from "../i18n";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { Users, UserPlus, UserMinus, TrendingDown, Wallet, CheckSquare, Clock, Umbrella } from "lucide-react";

const COLORS = ["#1E40AF", "#059669", "#D97706", "#0891B2", "#E11D48", "#7C3AED"];

export default function Dashboard() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const isMgr = MGR_ROLES.includes(user?.role);

  useEffect(() => {
    if (isMgr) api.get("/analytics/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, [isMgr]);

  if (!isMgr) return <EmployeeHome />;
  if (!data) return <p className="text-sm text-slate-500">{t("common.loading")}</p>;

  const stats = [
    { label: t("dash.headcount"), value: data.headcount, icon: Users, testid: "stat-headcount" },
    { label: t("dash.joiners"), value: data.joiners, icon: UserPlus, testid: "stat-joiners" },
    { label: t("dash.leavers"), value: data.leavers, icon: UserMinus, testid: "stat-leavers" },
    { label: t("dash.turnover"), value: `${data.turnover}%`, icon: TrendingDown, testid: "stat-turnover" },
    { label: t("dash.payrollcost"), value: fmtRM(data.monthly_cost), icon: Wallet, testid: "stat-payroll-cost", mono: true },
    { label: t("dash.pending"), value: data.pending_approvals, icon: CheckSquare, testid: "stat-pending" },
    { label: t("dash.late"), value: data.late_this_month, icon: Clock, testid: "stat-late" },
    { label: t("dash.leaveutil"), value: `${data.leave_utilisation}%`, icon: Umbrella, testid: "stat-leave-util" },
  ];

  return (
    <div data-testid="hr-dashboard" className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("dash.title")}</h1>
        <p className="text-sm text-slate-500 mt-1">{lang === "bm" ? `Selamat datang, ${user?.name}` : `Welcome back, ${user?.name}`}</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {stats.map((s) => (
          <div key={s.testid} data-testid={s.testid} className="stat-card">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-400">{s.label}</p>
              <s.icon className="w-4 h-4 text-blue-800 dark:text-blue-400" />
            </div>
            <p className={`mt-2 text-2xl sm:text-3xl font-extrabold tracking-tight ${s.mono ? "font-mono" : ""}`}>{s.value}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <Card className="lg:col-span-3">
          <CardHeader><CardTitle className="text-base">{t("dash.costchart")}</CardTitle></CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.cost_by_month}>
                <XAxis dataKey="period" fontSize={12} />
                <YAxis fontSize={12} tickFormatter={(v) => `${v / 1000}k`} />
                <Tooltip formatter={(v) => fmtRM(v)} />
                <Bar dataKey="gross" fill="#1E40AF" radius={[6, 6, 0, 0]} name="Gross" />
                <Bar dataKey="employer_cost" fill="#0891B2" radius={[6, 6, 0, 0]} name="Employer Cost" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="text-base">{t("dash.deptchart")}</CardTitle></CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.by_department} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={3}>
                  {data.by_department.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip /><Legend fontSize={12} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base">{t("dash.contracts")}</CardTitle></CardHeader>
        <CardContent>
          {data.contracts_expiring.length === 0 ? (
            <p className="text-sm text-slate-500">-</p>
          ) : (
            <div className="space-y-2">
              {data.contracts_expiring.map((c) => (
                <div key={c.employee_id} className="flex items-center justify-between border rounded-lg px-4 py-2.5">
                  <div>
                    <p className="text-sm font-medium">{c.name}</p>
                    <p className="text-xs text-slate-500">{c.department}</p>
                  </div>
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 font-mono">{fmtDate(c.contract_end)}</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function EmployeeHome() {
  const { user, employee } = useAuth();
  const { lang } = useLang();
  return (
    <div className="space-y-6" data-testid="employee-home">
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
        {lang === "bm" ? `Selamat datang, ${user?.name?.split(" ")[0]}` : `Welcome, ${user?.name?.split(" ")[0]}`}
      </h1>
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-slate-600">
            {lang === "bm"
              ? `Anda log masuk sebagai ${employee?.position || user?.role}. Gunakan "Portal Saya" untuk clock-in, payslip dan baki cuti anda.`
              : `You are signed in as ${employee?.position || user?.role}. Use "My Portal" for clock-in, payslips and leave balances.`}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
